import type { ProjectSort } from '~/shared/api'
import type { IMenuItem, SelectMultipleOptionType } from '~/shared/ui'
import { ProjectSortFields, ProjectThemeEnum, ProjectVisibilityEnum, ProjectWorkspacePresetEnum, RegistryContactTypeEnum, SortDirection } from '~/shared/api'
import { permissions } from '~/shared/lib'

export enum Nav {
  all = 'all',
  my = 'my',
  favorite = 'favorite',
  requests = 'requests',
}

export const OPTIONS_OF_NAV = [
  {
    label: 'Все проекты',
    value: Nav.all,
  },
  {
    label: 'Мои проекты',
    value: Nav.my,
  },
  {
    label: 'Избранные',
    value: Nav.favorite,
  },
  {
    label: 'Заявки',
    value: Nav.requests,
  },
]

export enum Sort {
  new = 'new',
  oldest = 'oldest',
}

export const SORT_CONFIG_MAP: Record<string, ProjectSort> = {
  new: { field: ProjectSortFields.StartDate, direction: SortDirection.Desc },
  oldest: { field: ProjectSortFields.StartDate, direction: SortDirection.Asc },
}

export const OPTIONS_OF_SORTS = [
  {
    value: Sort.new,
    label: 'Сначала новые',
  },
  {
    value: Sort.oldest,
    label: 'Сначала старые',
  },
]

export const ORDERED_THEMES: Array<ProjectThemeEnum> = [
  ProjectThemeEnum.All,
  ProjectThemeEnum.PortfolioResume,
  ProjectThemeEnum.Photo,
  ProjectThemeEnum.Design,
  ProjectThemeEnum.VideoCinema,
  ProjectThemeEnum.Literature,
  ProjectThemeEnum.Journalism,
  ProjectThemeEnum.Music,
  ProjectThemeEnum.Theater,
  ProjectThemeEnum.Nature,
  ProjectThemeEnum.Charity,
  ProjectThemeEnum.SocialInitiatives,
  ProjectThemeEnum.Ecology,
  ProjectThemeEnum.Education,
  ProjectThemeEnum.ScienceEnlightenment,
]

export const THEME_LABEL_MAP: Record<ProjectThemeEnum, string> = {
  [ProjectThemeEnum.All]: 'Все темы',
  [ProjectThemeEnum.PortfolioResume]: 'Портфолио и резюме',
  [ProjectThemeEnum.Photo]: 'Фото',
  [ProjectThemeEnum.Design]: 'Дизайн',
  [ProjectThemeEnum.VideoCinema]: 'Видео и кино',
  [ProjectThemeEnum.Literature]: 'Литература',
  [ProjectThemeEnum.Journalism]: 'Журналистика',
  [ProjectThemeEnum.Music]: 'Музыка',
  [ProjectThemeEnum.Theater]: 'Театр',
  [ProjectThemeEnum.Nature]: 'Природа',
  [ProjectThemeEnum.Charity]: 'Благотворительность',
  [ProjectThemeEnum.SocialInitiatives]: 'Общественные инициативы',
  [ProjectThemeEnum.Ecology]: 'Экология',
  [ProjectThemeEnum.Education]: 'Обучение',
  [ProjectThemeEnum.ScienceEnlightenment]: 'Наука и просвещение',
}

export const THEME_ICON_MAP: Record<ProjectThemeEnum, string> = {
  [ProjectThemeEnum.All]: 'vuesax:element-4-twotone',
  [ProjectThemeEnum.PortfolioResume]: 'vuesax:personalcard-twotone',
  [ProjectThemeEnum.Photo]: 'vuesax:gallery-twotone',
  [ProjectThemeEnum.Design]: 'vuesax:brush-2-twotone',
  [ProjectThemeEnum.VideoCinema]: 'vuesax:video-square-twotone',
  [ProjectThemeEnum.Literature]: 'vuesax:book-twotone',
  [ProjectThemeEnum.Journalism]: 'vuesax:microphone-2-twotone',
  [ProjectThemeEnum.Music]: 'vuesax:musicnote-twotone',
  [ProjectThemeEnum.Theater]: 'vuesax:emoji-normal-twotone',
  [ProjectThemeEnum.Nature]: 'vuesax:tree-twotone',
  [ProjectThemeEnum.Charity]: 'vuesax:heart-linear',
  [ProjectThemeEnum.SocialInitiatives]: 'vuesax:people-twotone',
  [ProjectThemeEnum.Ecology]: 'vuesax:global-twotone',
  [ProjectThemeEnum.Education]: 'vuesax:message-question-twotone',
  [ProjectThemeEnum.ScienceEnlightenment]: 'vuesax:3dcube-twotone',
}

export const OPTIONS_OF_THEME = ORDERED_THEMES.map(value => ({
  value,
  label: THEME_LABEL_MAP[value],
  icon: THEME_ICON_MAP[value],
}))

export const PROJECT_TYPE_LABEL_MAP: Record<ProjectVisibilityEnum, string> = {
  [ProjectVisibilityEnum.Open]: 'Открытый',
  [ProjectVisibilityEnum.Closed]: 'Закрытый',
  [ProjectVisibilityEnum.Hidden]: 'Скрытый',
}

export const OPTIONS_OF_PROJECT_TYPE = Object.entries(
  PROJECT_TYPE_LABEL_MAP,
).map(([value, label]) => ({
  value: value as ProjectVisibilityEnum,
  label,
}))

export const FILTER_PROJECT_VISIBILITY_ALL = 'all'

export function getOptionsOfProjectTypeFilter(options?: { withHidden?: boolean }) {
  return [
    {
      value: FILTER_PROJECT_VISIBILITY_ALL,
      label: 'Все',
    },
    ...OPTIONS_OF_PROJECT_TYPE.filter(
      item => options?.withHidden || item.value !== ProjectVisibilityEnum.Hidden,
    ),
  ]
}

export const OPTIONS_OF_PROJECT_TYPE_FILTER = getOptionsOfProjectTypeFilter()

export const FILTER_THEME_OPTIONS = ORDERED_THEMES
  .filter(theme => theme !== ProjectThemeEnum.All)
  .map(theme => ({
    value: theme,
    label: THEME_LABEL_MAP[theme],
    icon: THEME_ICON_MAP[theme],
  }))

export const OPTIONS_OF_SUPPORTER_TYPE: SelectMultipleOptionType[] = [
  {
    label: 'Физ. лица',
    value: RegistryContactTypeEnum.Individual,
    type: 'multiple',
  },
  {
    label: 'Юр. лица',
    value: RegistryContactTypeEnum.Organization,
    type: 'single',
  },
  {
    label: 'Участники',
    value: RegistryContactTypeEnum.ProjectMember,
    type: 'multiple',
  },
]

export const PROJECT_FORM_DRAFT_STORAGE_KEY = 'kvest-project-form-draft'

export const PROJECT_FORM_LEAVE_EXCLUDE_ROUTE_NAMES: string[] = [
  permissions.PROJECTS.REQUESTS.EDIT.PERMISSION,
  permissions.PROJECTS.REQUESTS.WORKSPACE_PRESET_SELECT.PERMISSION,
]

export enum ProjectCreationType {
  classic = 'classic',
  templateForDeputies = 'template-for-deputies',
  expertOpinion = 'expert-opinion',
}

export const OPTIONS_OF_PROJECT_CREATION_TYPE: IMenuItem<ProjectCreationType>[] = [
  {
    label: 'Классический',
    name: ProjectCreationType.classic,
  },
  {
    label: 'Типовой проект',
    name: ProjectCreationType.templateForDeputies,
  },
  {
    label: 'Экспертное мнение',
    name: ProjectCreationType.expertOpinion,
  },
]

export const OPTIONS_OF_PROJECT_CREATION_KIND = OPTIONS_OF_PROJECT_CREATION_TYPE.map(
  o => ({ value: o.name, label: o.label }),
)

export const WORKSPACE_PRESET_LABEL_MAP: Record<ProjectWorkspacePresetEnum, string> = {
  [ProjectWorkspacePresetEnum.Zhkh]: 'ЖКХ',
}

export const WORKSPACE_PRESET_CARDS: Array<{
  preset: ProjectWorkspacePresetEnum
  title: string
  description: string
}> = [
  {
    preset: ProjectWorkspacePresetEnum.Zhkh,
    title: 'ЖКХ',
    description:
      'Создание → Отчеты Мгновенные решения и обратная связь. Аналитика вовлеченности за 2 клика.',
  },
]

export type WorkspacePresetCard = (typeof WORKSPACE_PRESET_CARDS)[number]

export enum WorkspaceModuleMenuAction {
  ModuleSettings = 'module-settings',
  Edit = 'edit',
  Delete = 'delete',
}

export const WORKSPACE_MODULE_SETTINGS_MENU_ITEM: IMenuItem = {
  name: WorkspaceModuleMenuAction.ModuleSettings,
  label: 'Настройки модуля',
  icon: 'vuesax:setting-3',
  iconOptions: {
    fillType: 'outline',
  },
}

export const WORKSPACE_NATIVE_MODULE_SETTINGS_MENU_ITEMS: IMenuItem[] = [
  WORKSPACE_MODULE_SETTINGS_MENU_ITEM,
]

export const WORKSPACE_MODULE_EDIT_MENU_ITEM: IMenuItem = {
  name: WorkspaceModuleMenuAction.Edit,
  label: 'Редактировать',
  icon: 'vuesax:edit-linear',
  iconOptions: {
    fillType: 'outline',
  },
}

export const WORKSPACE_MODULE_DELETE_MENU_ITEMS: IMenuItem[] = [
  {
    name: WorkspaceModuleMenuAction.Delete,
    label: 'Удалить',
    icon: 'vuesax:trash-linear',
    iconOptions: {
      fillType: 'outline',
    },
  },
]

export enum EmergencyContactMenuItem {
  Edit = 'edit',
  Delete = 'delete',
}

export const EMERGENCY_CONTACT_MENU_ITEMS: IMenuItem[] = [
  {
    name: EmergencyContactMenuItem.Edit,
    label: 'Редактировать',
    icon: 'vuesax:edit-linear',
    iconOptions: {
      fillType: 'outline',
    },
  },
  {
    name: EmergencyContactMenuItem.Delete,
    label: 'Удалить',
    icon: 'vuesax:trash-linear',
    iconOptions: {
      fillType: 'outline',
    },
  },
]
